import type { InferType } from 'yup';

import authInstance from '@/lib/axios/auth-instance';
import type { IBaseResponse } from '@/types/base-response';
import type {
  forgotSchema,
  loginSchema,
  resetPasswordSchema,
} from './auth.validation';
import type {
  IAccessToken,
  IExportChallenge,
  IExportConfirmation,
  IForgotToken,
  IPasswordChangeChallenge,
  ITwoFactorChallenge,
  ITwoFactorResend,
  ITotpChallenge,
  ITotpConfirmed,
  ITotpSecret,
  ITotpSetupChallenge,
  ITotpStatus,
  IUser,
  IViewAsStart,
} from './auth.types';
import { AxiosError } from 'axios';
import bookingInstance from '@/lib/axios/booking-instance';

const login = async (
  data: InferType<typeof loginSchema> & { trusted_device_token?: string }
): Promise<
  | IBaseResponse<
      | IAccessToken
      | ITwoFactorChallenge
      | IPasswordChangeChallenge
      | ITotpChallenge
      | ITotpSetupChallenge
    >
  | undefined
> => {
  try {
    const response = await authInstance.post('/auth/login', data);

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    }

    throw error;
  }
};

const setPassword = async (data: {
  password_change_token: string;
  password: string;
  passConfirmation: string;
  trusted_device_token?: string;
}): Promise<
  IBaseResponse<
    IAccessToken | ITwoFactorChallenge | ITotpChallenge | ITotpSetupChallenge
  >
> => {
  try {
    const response = await authInstance.post('/auth/set-password', data);

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    }

    throw error;
  }
};

const verifyTwoFactor = async (data: {
  challenge_id: string;
  code: string;
  remember_device: boolean;
}): Promise<IBaseResponse<IAccessToken>> => {
  try {
    const response = await authInstance.post('/auth/2fa/verify', data);

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    }

    throw error;
  }
};

const resendTwoFactor = async (data: {
  challenge_id: string;
}): Promise<IBaseResponse<ITwoFactorResend>> => {
  try {
    const response = await authInstance.post('/auth/2fa/resend', data);

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    }

    throw error;
  }
};

// Booking CSV exports are confirmed with an emailed code, on every tenant and
// whether or not login 2FA is on.
const startExportConfirmation = async (): Promise<
  IBaseResponse<IExportChallenge>
> => {
  try {
    const response = await authInstance.post('/auth/export-confirmation');

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    }

    throw error;
  }
};

const verifyExportConfirmation = async (data: {
  challenge_id: string;
  code: string;
}): Promise<IBaseResponse<IExportConfirmation>> => {
  try {
    const response = await authInstance.post(
      '/auth/export-confirmation/verify',
      data
    );

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    }

    throw error;
  }
};

const resendExportConfirmation = async (data: {
  challenge_id: string;
}): Promise<IBaseResponse<ITwoFactorResend>> => {
  try {
    const response = await authInstance.post(
      '/auth/export-confirmation/resend',
      data
    );

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    }

    throw error;
  }
};

const bearer = (token: string) => ({ Authorization: `Bearer ${token}` });

// The authenticator app endpoints. On a login (`setup_token`, `challenge_id`)
// they go without a token; from the profile the interceptor adds the user's.
const totpPost = async <T>(
  path: string,
  data: object
): Promise<IBaseResponse<T>> => {
  try {
    const response = await authInstance.post(`/auth/2fa/totp/${path}`, data);

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    }

    throw error;
  }
};

// Exactly one of `code` or `recovery_code`.
const verifyTotp = (
  data: { challenge_id: string } & (
    | { code: string }
    | { recovery_code: string }
  )
) => totpPost<IAccessToken>('verify', data);

const setupTotp = (data: { setup_token?: string }) =>
  totpPost<ITotpSecret>('setup', data);

const confirmTotp = (data: { setup_token?: string; code: string }) =>
  totpPost<ITotpConfirmed>('confirm', data);

const regenerateRecoveryCodes = (data: { code: string }) =>
  totpPost<ITotpConfirmed>('recovery-codes', data);

const disableTotp = (data: { password: string; code: string }) =>
  totpPost<null>('disable', data);

// A super Admin removes another user's app, confirmed with their own code.
const resetTotp = (data: { user_id: string; code: string }) =>
  totpPost<null>('reset', data);

const getTotpStatus = async (): Promise<IBaseResponse<ITotpStatus>> => {
  const response = await authInstance.get('/auth/2fa/totp/status');

  return response.data;
};

// Without a token, ends whichever session the interceptor sends (a view-as
// token while one is running). A view-as token is ended by naming it.
const logout = async (token?: string): Promise<IBaseResponse> => {
  const response = await authInstance.post('/auth/logout', undefined, {
    headers: token ? bearer(token) : undefined,
  });

  return response.data;
};

// Always on the Admin's own token: a view-as token is refused (403), even to
// switch to another role.
const startViewAs = async ({
  adminToken,
  ...data
}: {
  adminToken: string;
  companyId: string;
  roleId: string;
  branchIds?: string[];
}): Promise<IBaseResponse<IViewAsStart>> => {
  try {
    const response = await authInstance.post('/auth/view-as', data, {
      headers: bearer(adminToken),
    });

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    }

    throw error;
  }
};

const verify = async (): Promise<IBaseResponse<IUser>> => {
  const response = await authInstance.get('/auth/verify');

  return response.data;
};

const forgotPassword = async (
  data: InferType<typeof forgotSchema>
): Promise<IBaseResponse<IForgotToken>> => {
  try {
    const response = await bookingInstance.post('/user/verify-email', data);

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    }

    throw error;
  }
};

const resetPassword = async (
  data: InferType<typeof resetPasswordSchema>
): Promise<IBaseResponse> => {
  try {
    const response = await bookingInstance.post('/user/change-password', data);

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error.response?.data;
    }

    throw error;
  }
};

export const AuthService = {
  login,
  setPassword,
  verifyTwoFactor,
  resendTwoFactor,
  verifyTotp,
  setupTotp,
  confirmTotp,
  getTotpStatus,
  regenerateRecoveryCodes,
  disableTotp,
  resetTotp,
  startExportConfirmation,
  verifyExportConfirmation,
  resendExportConfirmation,
  logout,
  startViewAs,
  verify,
  forgotPassword,
  resetPassword,
};
